<template>
  <div
    ref="readinessBar"
    class="employee-readiness-bar"
    :class="{
      'recalc-active1': isRecalc,
      'employee-readiness-bar-importance': showImportance,
      'employee-readiness-bar-no-assignments': status === 'No Assignments'
    }"
    :style="onlyReadinessStyles"
  >
    <div v-if="status === 'No Assignments'">
      <span class="no-assignment-text"> {{ noAssignmentsText }}</span>
    </div>
    <div
      v-else-if="status === 'Only Readiness'"
      class="d-flex only-readiness-content"
      :class="{ 'only-readiness-content--hover': enableHover }"
    >
      <ReadinessBar
        :barValue="animatedReadiness"
        type="readiness"
        :loading="loading"
        class="readiness-bar-wrapper"
        :class="{
          'readiness-bar-wrapper-importance': showImportance,
        }"
      />
    </div>
    <div v-else class="d-flex align-center">
      <ImportanceSelector
        v-if="showImportance"
        class="importance-selector-tree pr-2"
        :importanceLevel="importanceLevel"
        :importanceSelector="true"
        :totalSteps="3"
        :editable="true"
        :read_only="false"
        :dropdownItems="dropdownItems"
        :translateXOffset="-8"
        :translateYOffset="-16"
        @weightChanged="onImportanceChange"
      ></ImportanceSelector>
      <ReadinessBar
        :barValue="animatedReadiness"
        type="readiness"
        :loading="loading"
        class="readiness-bar-wrapper"
        :class="{
          'readiness-bar-wrapper-importance': showImportance,
        }"
      />
      <div
        v-if="status === 'Recalc in Progress' || isEmployeeRecalc"
        class="spinner-container"
      >
        <v-icon>$ex-primary-spinner</v-icon>
      </div>
      <div
         v-else-if="open"
        ref="menuActivator"
        class="employee-readines-icon-container"
        @click="toggleMenu"
      >
        <img
          v-if="isMenuOpened"
          src="../../components/assets/upCaretIcon.svg"
          alt=""
        />
        <img v-else src="../../components/assets/downCaretIcon.svg" alt="" />
      </div>
      <v-menu
        v-model="isMenuOpened"
        :content-class="`employee-readiness-menu`"
        :position-x="positionX"
        :position-y="positionY"
        :close-on-content-click="false"
        :close-on-click="true"
        eager
        :attach="attachSelector"
        :absolute="true"
      >
        <div v-if="isRecalc" class="employee-readiness-calc">
          <ExButton
            label="Recalculate"
            :must="true"
            :normal="'normal-hover'"
            size="normal"
            @onClick="onActionClick()"
          />
        </div>
        <div class="employee-readiness-position-block">
          <div class="d-flex employee-readiness-position">
            <span class="employee-readiness-code">{{ code }}</span>
            <span class="employee-readiness-name">{{ name }}</span>
          </div>
        </div>
      </v-menu>
    </div>
  </div>
</template>
<script setup>
import {
  ref,
  onMounted,
  onBeforeUnmount,
  nextTick,
  computed,
  defineEmits,
  watch,
} from "vue";
import ReadinessBar from "@components/NewReadinessBar/ReadinessBar.vue";
import ExButton from "@components/buttons/Button.vue";
import ImportanceSelector from "@components/ImportanceSelector/ImportanceSelector.vue";
const emit = defineEmits(["onActionClick", "importanceChanged"]);
const props = defineProps({
  status: {
    type: String,
    required: true,
    validator: (value) =>
      [
        "No Assignments",
        "Has Assignments",
        "Recalc in Progress",
        "Only Readiness",
      ].includes(value),
  },
  recalc: { type: Boolean, default: false },
  open: { type: Boolean, default: false },
  history: { type: Boolean, default: false },
  code: { type: String, default: "" },
  name: { type: String, default: "" },
  readiness: { type: Number, default: null },
  showImportance: { type: Boolean, default: false },
  importance: { type: [String, Number], default: "" },
  noAssignmentsText: { type: String, default: "No Positions Assignments" },
  enableHover: { type: Boolean, default: true },
  loading: { type: Boolean, default: false },
});

const importanceLevel = computed(() => {
  const val = Number(props.importance);
  return isNaN(val) ? 0 : val;
});

const animatedReadiness = ref(props.readiness);
const intervalId = ref(null);

const animateValue = (newValue) => {
  if (intervalId.value) {
    clearInterval(intervalId.value);
    intervalId.value = null;
  }

  if (newValue === null || newValue === undefined) {
    animatedReadiness.value = null;
    return;
  }

  const targetValue = Number(newValue);
  const startValue = animatedReadiness.value === null ? 0 : Number(animatedReadiness.value);
  
  if (startValue === targetValue) {
    animatedReadiness.value = targetValue;
    return;
  }

  const duration = 1500; // Match the header animation duration
  const steps = 30;
  const stepValue = (targetValue - startValue) / steps;
  let currentStep = 0;

  intervalId.value = setInterval(() => {
    currentStep++;
    if (currentStep >= steps) {
      animatedReadiness.value = targetValue;
      clearInterval(intervalId.value);
      intervalId.value = null;
    } else {
      animatedReadiness.value = startValue + (stepValue * currentStep);
    }
  }, duration / steps);
};

watch(() => props.readiness, (newVal) => {
  animateValue(newVal);
});

onBeforeUnmount(() => {
  if (intervalId.value) {
    clearInterval(intervalId.value);
  }
});

const isMenuOpened = ref(false);
const positionX = ref(0);
const positionY = ref(0);
const attachSelector = "body";
const menuActivator = ref(null);
const readinessBar = ref(null);
const isEmployeeRecalc = ref(false);
const dropdownItems = ref([
  { name: "High", value: 3 },
  { name: "Medium", value: 2 },
  { name: "Low", value: 1 },
  { name: "None", value: 0 },
]);
const toggleMenu = async () => {
  isMenuOpened.value = !isMenuOpened.value;
  if (isMenuOpened.value) {
    await nextTick();
    updateMenuPosition();
  }
};
const isOpened = computed(
  () =>
    props.status !== "Recalc in Progress" &&
    props.status !== "No Assignments" &&
    props.open
);
const isRecalc = computed(
  () => props.status === "Recalc in Progress" || props.recalc
);
const onlyReadinessStyles = computed(() => {
  if (props.status === "Only Readiness") {
    return {
      border: "none",
      background: "transparent",
      boxShadow: "none",
    };
  }
  return {};
});

const updateMenuPosition = () => {
  if (menuActivator.value) {
    const rect = menuActivator.value.getBoundingClientRect();
    positionX.value = rect.left - 162;
    positionY.value = rect.top + menuActivator.value.offsetHeight + 10;
  }
};

const handleViewportChange = () => {
  if (isMenuOpened.value) {
    updateMenuPosition();
  }
};
const onActionClick = () => {
  isEmployeeRecalc.value = true;
  isMenuOpened.value = false;
  emit("onActionClick");
};

const onImportanceChange = (newValue) => {
  emit("importanceChanged", newValue);
};

onMounted(() => {
  window.addEventListener("scroll", handleViewportChange, true);
  window.addEventListener("resize", handleViewportChange, true);
  if (window.visualViewport) {
    window.visualViewport.addEventListener("resize", handleViewportChange);
    window.visualViewport.addEventListener("scroll", handleViewportChange);
  }
  nextTick(() => {
    if (isOpened.value) {
      updateMenuPosition();
    }
  });
});

onBeforeUnmount(() => {
  window.removeEventListener("scroll", handleViewportChange, true);
  window.removeEventListener("resize", handleViewportChange, true);
  if (window.visualViewport) {
    window.visualViewport.removeEventListener("resize", handleViewportChange);
    window.visualViewport.removeEventListener("scroll", handleViewportChange);
  }
});

watch(isMenuOpened, async (opened) => {
  if (!opened) return;
  await nextTick();
  updateMenuPosition();
});
</script>

<style scoped>
.employee-readiness-bar {
  margin: 20px;
  width: 195px;
  padding: 6px 8px;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  border-radius: var(--radius-md, 8px);
  border: 1px solid #d0d5dd;
  background: #fff;
  box-shadow: 0px 1px 2px 0px rgba(16, 24, 40, 0.05);
  position: relative;
}
.employee-readiness-bar-importance {
  padding: 0px 8px;
}
.employee-readiness-bar-no-assignments {
  padding: 6px 8px;
}
.only-readiness-content {
  padding: 6px 10px 10px 10px;
  position: relative;
  left: -13px;
}
.only-readiness-content.only-readiness-content--hover:hover {
  cursor: url("../assets/cursorArrow.svg"), auto;
  border-radius: 8px;
  background: #f2edff;
}
.employee-readiness-bar.recalc-active {
  background: #feffd2;
}
.employee-readiness-bar .no-assignment-text {
  color: #344054;
  font-family: Inter;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 20px;
  margin-left: 2px;
}
.employee-readiness-bar .readiness-bar-wrapper {
  flex-grow: 1;
  margin-top: 2px;
}
.employee-readiness-bar .readiness-bar-wrapper .progress-value {
  padding-left: 2px !important;
}
.employee-readiness-bar .spinner-container {
  margin-left: 10px;
  margin-bottom: -2px;
  margin-top: -1px;
}
.employee-readiness-bar .employee-readines-icon-container {
  height: 24px;
  width: 24px;
  border-radius: 100%;
  display: flex;
  justify-items: center;
  align-items: center;
  margin-left: 4px;
}
.employee-readiness-bar .employee-readines-icon-container:hover {
  cursor: url("../assets/cursorArrow.svg"), auto;
  background: #f4f4f4;
}
.employee-readiness-bar .employee-readines-icon-container img {
  margin-left: 5px;
}
.employee-readiness-menu {
  min-width: 195px;
  transform-origin: center top;
  width: 195px;
  border: 1px solid #d0d5dd;
  border-radius: 8px;
  border: 1px solid #eaecf0;
  background: #fff;
  box-shadow: 0px 12px 16px -4px rgba(16, 24, 40, 0.08),
    0px 4px 6px -2px rgba(16, 24, 40, 0.03);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  align-self: stretch;
}
.employee-readiness-position {
  margin-top: -5px !important;
  padding: 0 17px;
  overflow: hidden;
  white-space: nowrap;
  display: flex;
  align-items: center;
  width: 100%;
}
.employee-readiness-code {
  color: #475467;
  font-family: Inter;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 24px;
  flex-shrink: 0;
}
.employee-readiness-name {
  overflow: hidden;
  color: #101828;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-family: Inter;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 24px;
  margin-left: 8px;
  min-width: 0;
  flex: 1;
}
.employee-readiness-calc {
  display: flex;
  padding: 16px 0px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  align-self: stretch;
  border-bottom: 1px solid #eaecf0;
}
.employee-readiness-bar {
  position: relative;
  left: -20px;
}
.employee-readines-icon-container {
  cursor: pointer;
}
.employee-readiness-history {
  margin-top: 4px;
  margin-bottom: -4px;
  padding-left: 17px;
  display: flex;
  justify-content: space-evenly;
  margin-left: -16px;
}
::v-deep.employee-readiness-history .bar-box {
  width: 15px !important;
  height: 15px !important;
  display: flex;
  flex-direction: column-reverse;
}
::v-deep.employee-readiness-history .flex-column {
  width: 15px !important;
}
::v-deep.employee-readiness-history .col-auto {
  padding: 5px !important;
}
.employee-readiness-position-block {
  padding: 12px 0px 12px 0px;
  width: 100%;
  box-sizing: border-box;
}
.readiness-divider {
  height: 2px;
  background-color: #eaecf0;
  width: 100%;
  margin: 8px 0;
}
::v-deep .importance-selector-tree .ex-generic-selector-default-activator {
  cursor: url("../assets/cursorArrow.svg"), auto !important;
}
::v-deep.readiness-bar-wrapper .new-progress-bar-container {
  width: 110px;
}
::v-deep.readiness-bar-wrapper-importance .new-progress-bar-container {
  width: 65px;
}
</style>
